import { StateCreator } from 'zustand';
import type { SortingState } from '@tanstack/react-table';

import type {
  RemittanceSource,
  RemittanceStatus,
} from '../../services/api/types.gen';

export type BillingTabKey =
  | 'not-ready'
  | 'ready'
  | 'pending-approval'
  | 'ready-to-send'
  | 'pending-payment'
  | 'paid'
  | 'rejected-cancelled';

/** Persisted toolbar state for one billing tab. pageIndex is intentionally NOT here. */
export interface BillingTabUiState {
  /** Server-side free-text search (debounced in the toolbar before write). */
  globalFilter: string;
  /** Selected insurance ids, sent as insurance_ids to the backend. */
  insuranceIds: string[];
  /** Selected clinic ids, sent as clinic_ids to the backend. */
  clinicIds: string[];
  /** Single-column sort; sorting[0] maps to sort_by/sort_order (server is single-sort). */
  sorting: SortingState;
  /** Server page size (per_page). pageIndex is NOT persisted. */
  pageSize: number;
}

export const DEFAULT_BILLING_TAB_UI: BillingTabUiState = {
  globalFilter: '',
  insuranceIds: [],
  clinicIds: [],
  sorting: [],
  pageSize: 25,
};

export const BILLING_TAB_DEFAULT_SORTING: Record<BillingTabKey, SortingState> =
  {
    'not-ready': [{ id: 'claim_age', desc: true }],
    ready: [{ id: 'claim_age', desc: true }],
    'pending-approval': [{ id: 'claim_age', desc: true }],
    'ready-to-send': [{ id: 'claim_age', desc: true }],
    'pending-payment': [{ id: 'claim_age', desc: true }],
    'rejected-cancelled': [{ id: 'claim_age', desc: true }],
    paid: [{ id: 'claim_age', desc: true }],
  };

/** Per-tab default state, memoized so `getBillingTabUi` returns a STABLE reference
 *  for unpersisted tabs. Zustand compares snapshots by identity; returning a fresh
 *  object each call triggers a "getSnapshot should be cached" infinite render loop. */
const DEFAULT_BILLING_TAB_UI_BY_TAB: Record<BillingTabKey, BillingTabUiState> =
  Object.fromEntries(
    (Object.keys(BILLING_TAB_DEFAULT_SORTING) as BillingTabKey[]).map(tab => [
      tab,
      { ...DEFAULT_BILLING_TAB_UI, sorting: BILLING_TAB_DEFAULT_SORTING[tab] },
    ])
  ) as Record<BillingTabKey, BillingTabUiState>;

export function defaultBillingTabUi(tab: BillingTabKey): BillingTabUiState {
  return DEFAULT_BILLING_TAB_UI_BY_TAB[tab];
}

/** Column ids the table currently renders as sortable (`enableSorting`) and that the
 *  backend can order by. A persisted sort referencing any other id is stale — e.g. the
 *  removed client-side `days_to_pay` / `total_anticipated_payment` / `tob` columns, or
 *  the now sort-disabled `total_charges`. The backend silently falls back to default
 *  ordering for an unknown `sort_by`, so such sorts must be dropped to the tab default
 *  during migration rather than sent verbatim. */
const SUPPORTED_BILLING_SORT_IDS: ReadonlySet<string> = new Set([
  'patient_name',
  'clinic_name',
  'insurance_name',
  'billing_period',
  'claim_age',
  'claim_number',
  'branch',
]);

/** Resolve a persisted sort to a single supported entry, else the tab default.
 *  The server is single-sort, so only the first sort entry is meaningful. */
function sanitizeBillingSorting(
  sorting: SortingState | undefined,
  tab: BillingTabKey
): SortingState {
  const first = sorting?.[0];
  if (first && SUPPORTED_BILLING_SORT_IDS.has(first.id)) {
    return [first];
  }
  return BILLING_TAB_DEFAULT_SORTING[tab];
}

/** One-time migration of persisted state:
 *  - sanitize persisted sorting: keep a supported single-column sort, otherwise fall
 *    back to the tab default urgency sort (covers stale EMPTY sorts and sorts on
 *    columns no longer server-sortable);
 *  - drop legacy client-side `columnFilters` (ROG-2913 replaced them with
 *    `insuranceIds`/`clinicIds`) and ensure the new filter arrays exist for any
 *    pre-migration persisted entry. */
export function migrateBillingUiState(
  state: Partial<Record<BillingTabKey, BillingTabUiState>>
): Partial<Record<BillingTabKey, BillingTabUiState>> {
  const next: Partial<Record<BillingTabKey, BillingTabUiState>> = { ...state };
  (Object.keys(next) as BillingTabKey[]).forEach(tab => {
    const entry = next[tab] as
      | (Partial<BillingTabUiState> & { columnFilters?: unknown })
      | undefined;
    if (!entry) return;
    const { columnFilters: _legacyColumnFilters, ...rest } = entry;
    void _legacyColumnFilters;
    next[tab] = {
      globalFilter: rest.globalFilter ?? '',
      sorting: sanitizeBillingSorting(rest.sorting, tab),
      pageSize: rest.pageSize ?? 25,
      insuranceIds: rest.insuranceIds ?? [],
      clinicIds: rest.clinicIds ?? [],
    };
  });
  return next;
}

/** Persisted toolbar state for the Remittances view. pageIndex is intentionally NOT here.
 *  Deposit dates are ISO 'YYYY-MM-DD' STRINGS, never Date objects: this slice is
 *  JSON-persisted, so a Date rehydrates from localStorage as a string while still
 *  typed Date, which crashes DatePicker and any .getTime()/.toISOString() call. */
export interface RemittanceUiState {
  /** Server-side free-text search (debounced in the toolbar before write). */
  globalFilter: string;
  /** The selected status tab, sent as the single `status` param. This is
   *  navigation, not a filter, so `resetRemittanceUi` preserves it — clearing a
   *  claims tab's toolbar likewise leaves you on that tab. */
  statusTab: RemittanceStatus;
  /** RemittanceSource values, sent as repeated `source` params. */
  sources: RemittanceSource[];
  /** Payor ids, sent as repeated `payor_id` params. */
  payorIds: string[];
  depositDateFrom: string | null;
  depositDateTo: string | null;
  /** Single-column sort; sorting[0] maps to sort_by/sort_order (server is single-sort). */
  sorting: SortingState;
  pageSize: number;
}

/** Default sort is a VISIBLE, sortable column so the header arrow is truthful on
 *  first load (the backend's own no-sort fallback is received_at, which the table
 *  does not render). */
export const REMITTANCE_DEFAULT_SORTING: SortingState = [
  { id: 'deposit_date', desc: true },
];

/** The status tab shown on first visit — the head of the remittance pipeline. */
export const DEFAULT_REMITTANCE_STATUS_TAB: RemittanceStatus = 'saved';

/** Valid status tab ids, used to sanitize a persisted value on read. */
export const REMITTANCE_STATUS_TAB_IDS: ReadonlySet<string> = new Set<string>([
  'saved',
  'completed',
]);

/** Column ids the remittance table renders sortable AND the backend can order by. */
export const SUPPORTED_REMITTANCE_SORT_IDS: ReadonlySet<string> = new Set([
  'deposit_date',
  'check_date',
  'trace_number',
  'payer_display',
  'status',
  'source',
  'amount',
  'claim_count',
]);

/** Module-level constant = a STABLE reference. Zustand compares snapshots by
 *  identity; a store read that builds a fresh object per call triggers React's
 *  "getSnapshot should be cached" infinite render loop (the same trap the
 *  DEFAULT_BILLING_TAB_UI_BY_TAB comment above documents). remittanceUiState is
 *  therefore a plain non-optional field, NOT a getter. */
export const DEFAULT_REMITTANCE_UI: RemittanceUiState = {
  globalFilter: '',
  statusTab: DEFAULT_REMITTANCE_STATUS_TAB,
  sources: [],
  payorIds: [],
  depositDateFrom: null,
  depositDateTo: null,
  sorting: REMITTANCE_DEFAULT_SORTING,
  pageSize: 25,
};

export interface BillingUiSlice {
  billingUiState: Partial<Record<BillingTabKey, BillingTabUiState>>;
  getBillingTabUi: (tab: BillingTabKey) => BillingTabUiState;
  setBillingTabUi: (
    tab: BillingTabKey,
    patch: Partial<BillingTabUiState>
  ) => void;
  resetBillingTabUi: (tab: BillingTabKey) => void;
  remittanceUiState: RemittanceUiState;
  setRemittanceUi: (patch: Partial<RemittanceUiState>) => void;
  resetRemittanceUi: () => void;
}

export const billingUiSlice: StateCreator<BillingUiSlice> = (set, get) => ({
  billingUiState: {},
  getBillingTabUi: tab => get().billingUiState[tab] ?? defaultBillingTabUi(tab),
  setBillingTabUi: (tab, patch) =>
    set(state => ({
      billingUiState: {
        ...state.billingUiState,
        [tab]: {
          ...(state.billingUiState[tab] ?? defaultBillingTabUi(tab)),
          ...patch,
        },
      },
    })),
  resetBillingTabUi: tab =>
    set(state => ({
      billingUiState: {
        ...state.billingUiState,
        [tab]: defaultBillingTabUi(tab),
      },
    })),
  remittanceUiState: DEFAULT_REMITTANCE_UI,
  setRemittanceUi: patch =>
    set(state => ({
      remittanceUiState: { ...state.remittanceUiState, ...patch },
    })),
  // Reset clears the toolbar filters but keeps the caller on their current
  // status tab (see RemittanceUiState.statusTab).
  resetRemittanceUi: () =>
    set(state => ({
      remittanceUiState: {
        ...DEFAULT_REMITTANCE_UI,
        statusTab: state.remittanceUiState.statusTab,
      },
    })),
});
